import { inject } from '@angular/core';
import { AnalyticsService } from '../../../../../../core/services/analytics.service';
import { ANALYTICS_EVENTS } from '../../../../../../core/analytics/events';
import { createApiState } from '../../../../../../core/models/api-state';
import { isErr } from '../../../../../../core/models/api-result';
import type { ApiResult } from '../../../../../../core/models/api-result';
import type { CmdkItem } from '../../models/cmdk.model';
import type { CmdkProviderContext, CmdkRemoteProvider } from '../../models/cmdk-provider';

/**
 * The half of a remote provider that is identical for every kind: own an
 * `ApiState`, write loading/data/error into it, and throw away answers that
 * arrive after the query has moved on.
 *
 * A subclass supplies three things — `fetch()`, and the identity fields — and
 * inherits every rule below. That is deliberate: each of these was a bug in
 * some palette somewhere, and none of them should be re-decided per kind.
 *
 * AI CONTEXT — the four rules this base exists to enforce
 *
 * 1. **The state controller never leaves this file.** `ApiState<T>` is
 *    readonly by type and only the controller can write it, so exposing the
 *    controller would let the scheduler write results it did not fetch. The
 *    service schedules; the provider writes.
 *
 * 2. **Staleness is checked after the await, not before.** `ctx.signal` is a
 *    DISCARD token: `GraphQLClientService.queryResult()` accepts no
 *    `AbortSignal` and the Apollo promise is not cancellable, so the request
 *    always completes. The only discipline available is to check on the way
 *    back and drop the result. Without it, a slow response for "boa" lands
 *    after a fast one for "boardroom" and the list shows results for a query
 *    the user has already finished typing.
 *
 * 3. **An empty result is not an error and not a stand-down.** It keeps the
 *    group header and reads as "nothing matched here", which is information.
 *    `standDown()` — scope moved away, query below the floor — clears the
 *    group entirely so no header appears at all.
 *
 * 4. **Loading is set by the scheduler before the debounce, not here.** The
 *    skeleton should appear when we KNOW we are going to fetch, not 250ms
 *    later when the request actually leaves. See `CmdkSearchService`.
 */
export abstract class CmdkRemoteProviderBase implements CmdkRemoteProvider {
  abstract readonly id: string;
  abstract readonly kind: CmdkRemoteProvider['kind'];
  abstract readonly scope: CmdkRemoteProvider['scope'];
  abstract readonly minQueryLength: number;
  readonly idle = 'hide' as const;

  private readonly controller = createApiState<CmdkItem[]>([]);
  readonly state = this.controller;

  // Injected in the base rather than in each subclass: every remote kind must
  // report, and a kind that forgot would be invisible in exactly the way this
  // telemetry exists to prevent.
  private readonly analytics = inject(AnalyticsService);

  abstract isEnabled(ctx?: CmdkProviderContext): boolean;

  /**
   * The one thing a subclass must write. Return the items for this context,
   * or an error to surface as the group's banner.
   */
  protected abstract fetch(ctx: CmdkProviderContext): Promise<ApiResult<CmdkItem[]>>;

  /**
   * Remote sources never browse. Selecting the chip with an empty query is
   * explicit intent (`shouldRunProvider` lets it through), but there is no
   * bounded "list everything" call for any of these kinds — a wildcard query
   * against the estate is the one request guaranteed to be both slow and
   * useless. The chip shows nothing until the user types.
   */
  browse(): CmdkItem[] {
    return [];
  }

  /**
   * Present only to satisfy `CmdkProvider`. The scheduler calls `load()`; if
   * something ever calls this it would be a local-path caller treating a
   * remote provider as synchronous, and returning [] is the safe answer.
   */
  search(): CmdkItem[] {
    return [];
  }

  async load(ctx: CmdkProviderContext): Promise<void> {
    const startedAt = performance.now();
    const result = await this.fetch(ctx);
    const latencyMs = Math.round(performance.now() - startedAt);

    // Rule 2. Check FIRST, before touching state — a stale response must not
    // clear a newer request's loading flag either.
    //
    // A discard is still REPORTED. It is the debounce's effectiveness
    // expressed as a number: a high discard rate means the window is too
    // short and we are paying for answers nobody sees.
    if (ctx.signal.aborted) {
      this.report(ctx, latencyMs, 0, 'discarded');
      return;
    }

    if (isErr(result)) {
      this.controller.setError(result.error);
      this.report(ctx, latencyMs, 0, 'error');
      return;
    }
    const items = result.data ?? [];
    this.controller.setData(items);
    this.report(ctx, latencyMs, items.length, 'ok');
  }

  private report(
    ctx: CmdkProviderContext,
    latencyMs: number,
    resultCount: number,
    outcome: 'ok' | 'error' | 'discarded',
  ): void {
    this.analytics.capture(ANALYTICS_EVENTS.CMDK_SEARCH_COMPLETED, {
      provider: this.id as 'space' | 'person' | 'news',
      latency_ms: latencyMs,
      result_count: resultCount,
      // Length only, never the query itself — AGENTS.md § Analytics.
      query_length: ctx.query.length,
      // Zero-result rate is only interpretable split on this: without a
      // building Spaces changes document and People does not run at all.
      had_location: ctx.locationId !== null,
      outcome,
    });
  }

  standDown(): void {
    this.controller.reset();
  }

  /** Called by the scheduler the moment it decides a fetch will happen. */
  markLoading(): void {
    this.controller.setLoading();
  }
}
